import { faUser as faUserLight } from '@fortawesome/pro-light-svg-icons';
import { faClock, faHouse, faPaw, faPlus, faUser } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createStyles, makeStyles, Theme, Typography } from '@material-ui/core';
import { Period } from '@spike/model';
import clsx from 'clsx';
import { getDateBusinessHours } from 'components/Calendar3/FullCalendar/FullCalendarUtils';
import { useMarketplace } from 'hooks';
import { Service } from 'model/Service';
import moment, { Moment } from 'moment-timezone';
import React from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import {
    getServiceSessionConfig,
    getSessionsForDate,
    getSessionTypeLabel,
    groupSessionsByTime,
    SESSION_AREA_LABELS,
    SessionBusinessArea
} from 'model/ServiceSession';

const VISIBLE_SLOT_GROUPS = 3;

export interface SessionScheduleGridProps {
    area: SessionBusinessArea;
    week: Period;
    services: Array<Service>;
    onAddSlot: (service: Service, date: Moment) => void;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            // width 0 + minWidth 100% takes the available width without contributing min-content width.
            width: 0,
            minWidth: '100%',
            overflowX: 'auto',
            boxSizing: 'border-box',

            [theme.breakpoints.up('md')]: {
                padding: '0px 40px'
            }
        },
        grid: {
            minWidth: 1039,
            display: 'grid',
            gridTemplateColumns: '157px repeat(7, minmax(126px, 1fr))'
        },
        columnHeader: {
            gap: 4,
            display: 'flex',
            padding: '0px 8px 12px',
            flexDirection: 'column',
            alignItems: 'center'
        },
        columnTitle: {
            fontSize: 14,
            fontWeight: 500,
            lineHeight: 1
        },
        columnSubtitle: {
            gap: 5,
            fontSize: 12,
            display: 'flex',
            color: '#7A7A7A',
            alignItems: 'center'
        },
        servicesHeader: {
            fontSize: 14,
            fontWeight: 500,
            padding: '0px 11px 12px'
        },
        serviceCell: {
            gap: 6,
            height: 127,
            display: 'flex',
            padding: '11px',
            flexDirection: 'column',
            borderTop: 'solid 1px #D8D8D8',
            justifyContent: 'space-between'
        },
        serviceChip: {
            gap: 5,
            fontSize: 12,
            fontWeight: 500,
            display: 'flex',
            alignSelf: 'flex-start',
            alignItems: 'center',
            padding: '2px 11px',
            borderRadius: 60,
            backgroundColor: '#E4DDD5'
        },
        serviceName: {
            fontSize: 13,
            fontWeight: 500,
            lineHeight: 1.2
        },
        serviceSubtitle: {
            fontSize: 11,
            color: '#7A7A7A'
        },
        servicePets: {
            gap: 5,
            fontSize: 12,
            display: 'flex',
            color: '#7A7A7A',
            marginTop: 'auto',
            alignItems: 'center'
        },
        cell: {
            'gap': 4,
            'height': 127,
            'display': 'flex',
            'padding': 5,
            'overflow': 'hidden',
            'position': 'relative',
            'flexDirection': 'column',
            'borderTop': 'solid 1px #D8D8D8',
            'borderLeft': 'solid 1px #D8D8D8',
            'backgroundColor': '#ffffff',

            '&[data-clickable="true"]': {
                cursor: 'pointer'
            },
            '&[data-clickable="true"]:hover': {
                zIndex: 1,
                boxShadow: 'inset 0 0 0 1px #222222'
            },
            '&[data-clickable="true"]:hover $addSlot': {
                display: 'flex'
            },
            '&[data-muted="true"]': {
                backgroundColor: '#F6F6F6'
            }
        },
        lastRowCell: {
            borderBottom: 'solid 1px #D8D8D8'
        },
        lastColCell: {
            borderRight: 'solid 1px #D8D8D8'
        },
        addSlot: {
            gap: 4,
            margin: 'auto',
            fontSize: 14,
            fontWeight: 500,
            display: 'none',
            alignItems: 'center',
            fontFamily: 'Poppins'
        },
        session: {
            height: 24,
            minHeight: 24,
            padding: '2px 3px 2px 4px',
            borderRadius: 4,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: '#EFEFEF'
        },
        sessionTime: {
            'gap': 4,
            'fontSize': 12,
            'fontWeight': 500,
            'lineHeight': 1,
            'color': '#000000',
            'display': 'inline-flex',
            'alignItems': 'center',

            '& svg': {
                fontSize: 11,
                width: 14
            }
        },
        sessionStaffBadge: {
            'gap': 2,
            'height': 20,
            'minWidth': 29,
            'padding': '0px 5px',
            'fontSize': 11,
            'fontWeight': 500,
            'color': '#ffffff',
            'borderRadius': 3.5,
            'display': 'inline-flex',
            'alignItems': 'center',
            'justifyContent': 'center',
            'backgroundColor': '#000000',

            '& svg': {
                fontSize: 11
            }
        },
        more: {
            fontSize: 12,
            fontWeight: 500,
            lineHeight: 1
        },
        cellFooter: {
            display: 'flex',
            alignItems: 'center',
            marginTop: 'auto',
            minHeight: 20
        },
        totalBadge: {
            'gap': 3,
            'height': 20,
            'marginLeft': 'auto',
            'padding': '0px 6px',
            'fontSize': 11,
            'fontWeight': 500,
            'color': '#ffffff',
            'borderRadius': 3.5,
            'display': 'inline-flex',
            'alignItems': 'center',
            'backgroundColor': '#92B4A7',

            '& svg': {
                fontSize: 11
            }
        },
        emptyMessage: {
            fontSize: 15,
            padding: '40px 0px',
            textAlign: 'center',
            color: '#7A7A7A'
        }
    })
);

export const SessionScheduleGrid: React.FC<SessionScheduleGridProps> = props => {
    const classes = useStyles();
    const marketplace = useMarketplace();

    const days = useSelector((state: RootState) => state.serviceSessions.days);

    const dates: Array<Moment> = React.useMemo(() => {
        return Array.from({ length: 7 }, (_, index) => props.week.from.clone().add(index, 'days'));
    }, [props.week.from]);

    const staffCountOf = (date: Moment) => {
        const staffIds = props.services.flatMap(service =>
            getSessionsForDate(days, service.id!, date).map(session => session.staffId)
        );

        return new Set(staffIds.filter(Boolean)).size;
    };

    if (props.services.length === 0) {
        return (
            <div className={classes.root}>
                <Typography className={classes.emptyMessage}>
                    There are no {SESSION_AREA_LABELS[props.area]} services to configure here.
                </Typography>
            </div>
        );
    }

    return (
        <div className={classes.root}>
            <div className={classes.grid}>
                <Typography className={classes.servicesHeader}>{SESSION_AREA_LABELS[props.area]} Services</Typography>

                {dates.map(date => (
                    <div
                        key={date.format('YYYY-MM-DD')}
                        className={classes.columnHeader}
                    >
                        <Typography className={classes.columnTitle}>{date.format('ddd, DD MMM')}</Typography>
                        <div className={classes.columnSubtitle}>
                            <FontAwesomeIcon icon={faUser} />
                            {staffCountOf(date)} Staff
                        </div>
                    </div>
                ))}

                {props.services.map((service, serviceIndex) => {
                    const config = getServiceSessionConfig(service, props.area);
                    const isLastRow = serviceIndex === props.services.length - 1;

                    return (
                        <React.Fragment key={service.id}>
                            <div className={clsx(classes.serviceCell, { [classes.lastRowCell]: isLastRow })}>
                                {config.inHome && (
                                    <div className={classes.serviceChip}>
                                        <FontAwesomeIcon icon={faHouse} />
                                        Home Service
                                    </div>
                                )}

                                <div>
                                    <Typography className={classes.serviceName}>{service.name}</Typography>
                                    <Typography className={classes.serviceSubtitle}>
                                        {getSessionTypeLabel(config)}
                                        {service.olbEnabled ? ' · Online Booking' : ''}
                                    </Typography>
                                </div>

                                <div className={classes.servicePets}>
                                    <FontAwesomeIcon icon={faPaw} />
                                    {config.maxPets} {config.maxPets === 1 ? 'pet' : 'pets'}
                                </div>
                            </div>

                            {dates.map((date, dateIndex) => {
                                const sessions = getSessionsForDate(days, service.id!, date);
                                const slotGroups = groupSessionsByTime(sessions);
                                const visibleGroups = slotGroups.slice(0, VISIBLE_SLOT_GROUPS);
                                const hiddenCount = slotGroups.length - visibleGroups.length;
                                const isPast = date.isBefore(moment(), 'day');
                                const isClosed = marketplace.schedule
                                    ? !getDateBusinessHours(date, marketplace.schedule)
                                    : false;
                                const isLastCol = dateIndex === dates.length - 1;

                                return (
                                    <div
                                        key={`${service.id}-${date.format('YYYY-MM-DD')}`}
                                        data-muted={isPast || isClosed}
                                        data-clickable="true"
                                        className={clsx(classes.cell, {
                                            [classes.lastRowCell]: isLastRow,
                                            [classes.lastColCell]: isLastCol
                                        })}
                                        onClick={() => props.onAddSlot(service, date)}
                                    >
                                        {visibleGroups.map(group => (
                                            <div
                                                key={group.time}
                                                className={classes.session}
                                            >
                                                <span className={classes.sessionTime}>
                                                    <FontAwesomeIcon icon={faClock} />
                                                    {moment(group.time, 'HH:mm').format('h:mm a')}
                                                </span>
                                                <span className={classes.sessionStaffBadge}>
                                                    <FontAwesomeIcon icon={faUserLight} />
                                                    {group.staffCount}
                                                </span>
                                            </div>
                                        ))}

                                        {sessions.length > 0 && (
                                            <div className={classes.cellFooter}>
                                                {hiddenCount > 0 && (
                                                    <span className={classes.more}>{hiddenCount} more</span>
                                                )}
                                                <span className={classes.totalBadge}>
                                                    <FontAwesomeIcon icon={faUserLight} />
                                                    Total: {sessions.length}
                                                </span>
                                            </div>
                                        )}

                                        {sessions.length === 0 && !isPast && (
                                            <span className={classes.addSlot}>
                                                <FontAwesomeIcon icon={faPlus} />
                                                Add Slot
                                            </span>
                                        )}
                                    </div>
                                );
                            })}
                        </React.Fragment>
                    );
                })}
            </div>
        </div>
    );
};
